import i18n from "i18next";
import { type FC, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router";

import { useCapKeyDeleteMutation } from "src/app/features/EmvKernelProfile/subFeatures/CapKey/services/server/CapKeyApi";
import type { CapKeyEntity } from "src/app/features/EmvKernelProfile/subFeatures/CapKey/types/CapKeyEntity";
import type { ExtendedColumnsType } from "../../../../../../../components";
import { CSVImportButton } from "../../../../../../../components/FeatureButtons/CSVImportButton";
import type { AntCustomFormField, CommonFieldAttributes } from "../../../../../../../components/Form/types/types";
import { PropertyTable } from "../../../../../../../components/Table/components/PropertyTable";
import { Actions } from "../../../../../../../components/Table/types/types";
import { canAccess } from "../../../../../../../core/router/auth/permissions";
import { ResourceName, ResourceVariant } from "../../../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../../../core/router/types/routerTypes";
import { baseAuthApi } from "../../../../../../../core/service/baseApi";
import {
    showSuccessNotification,
    showWarningNotification,
} from "../../../../../../../core/setup/redux/state/notifications/notificationsState";

const getColumnsConfig = (t: Function): ExtendedColumnsType<CapKeyEntity>[] => {
    return [
        {
            title: t("emvKernelConfiguration.id"),
            key: "id",
            dataIndex: "id",
        },
        {
            title: t("emvKernelConfigurationCreateForm.rid"),
            key: "rid",
            dataIndex: "rid",
        },
        {
            title: t("emvKernelConfigurationCreateForm.publicKeyIndexShort"),
            key: "publicKeyIndex",
            dataIndex: "publicKeyIndex",
        },
        {
            title: t("emvKernelConfigurationCreateForm.publicKeyExponentShort"),
            key: "publicKeyExponent",
            dataIndex: "publicKeyExponent",
        },
    ];
};

export interface CapKeyTableProps extends AntCustomFormField<CapKeyEntity[]>, CommonFieldAttributes {}

export const CapKeysTable: FC<CapKeyTableProps> = ({ value = [], onChange, readOnly }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const resourceUrl = `${ResourceName.EMV_KERNEL_PROFILES}/${ResourceVariant.APAC}/${id}/${ResourceName.CAP_KEYS}`;

    const onDataChanged = (data: CapKeyEntity[]) => {
        if (onChange) {
            onChange(data);
        }
    };

    const onRowClick = (record: CapKeyEntity) => navigate(`${BASE_PATH}/${resourceUrl}/${FORM_ACTION.EDIT}/${record.id}`);

    const addCallback = () => {
        navigate(`${BASE_PATH}/${resourceUrl}/${FORM_ACTION.CREATE}`);
    };

    const [deleteTableItem] = useCapKeyDeleteMutation();

    const deleteCallback = (item: CapKeyEntity) => {
        deleteTableItem({
            emvKernelProfileId: id,
            id: item.id,
        })
            .unwrap()
            .then((payload) => performDeleteSuccess(payload))
            .catch(() => {
                dispatch(
                    showWarningNotification({
                        text: i18n.t("common.formDeleteError", {
                            id: item.id,
                        }),
                    }),
                );
            });

        const performDeleteSuccess = (payload: CapKeyEntity) => {
            if (payload) {
                notifyOkDelete(payload);
            }
            onDataChanged(value.filter((record) => record.id !== item.id));
        };
    };

    const notifyOkDelete = useCallback(
        (payload: CapKeyEntity) => {
            dispatch(
                showSuccessNotification({
                    text: i18n.t("common.formDeleteSuccess", {
                        id: payload.id,
                        resource: payload.name,
                    }),
                    autoCloseDisabled: false,
                }),
            );
        },
        [dispatch],
    );

    const hasAccess = canAccess(ResourceName.EMV_KERNEL_PROFILES, "xml");
    const handleImportSuccess = useCallback(() => {
        dispatch(baseAuthApi.util.invalidateTags([ResourceName.EMV_KERNEL_PROFILES]));
    }, [dispatch]);

    return (
        <PropertyTable<CapKeyEntity>
            getColumnsConfig={getColumnsConfig}
            name="capKeys_table"
            propertyAsRowKey="id"
            tableData={value || []}
            readOnly={readOnly || false}
            onDataChanged={onDataChanged}
            onRow={(record: CapKeyEntity) => {
                return {
                    onClick: () => onRowClick(record),
                };
            }}
            resourceUrl={resourceUrl}
            actions={[Actions.DELETE, Actions.ADD, Actions.COPY]}
            actionCallbacks={{ add: addCallback, delete: (item?: CapKeyEntity) => item && deleteCallback(item) }}
            addButtonName="capKeysTable_addButton"
            customActions={[
                <CSVImportButton
                    key="adb-import-btn"
                    translationKey="list.importCapKeys"
                    resourceName={`${ResourceName.EMV_KERNEL_PROFILES}/apac/${id}/${ResourceName.CAP_KEYS}`}
                    isDisabled={!hasAccess}
                    maxRows={1000}
                    onSuccess={handleImportSuccess}
                    format="adb"
                    warningMessage={i18n.t("csvImport.modal.warningEdit", { "0": "Cap Keys" })}
                />,
            ]}
        />
    );
};
